import { ComponentProps, ElementType, ReactNode, cloneElement } from "react";

import { cn } from "@oneleet/utils";
import { useDelayedLoading } from "@oneleet/utils/src/hooks";

import { type VariantProps, cva } from "class-variance-authority";

import { Loader } from "@oneleet/components/src/atoms";

const DEFAULT_VARIANT = "outlined";
const DEFAULT_COLOR = "neutral";
const DEFAULT_SIZE = "base";
const DEFAULT_IS_DISABLED = false;

const button = cva(
  "font-ng-medium text-ng-md flex h-min w-min shrink-0 select-none items-center justify-center gap-2.5 whitespace-nowrap text-neutral-950 transition-colors duration-300 hover:duration-0",
  {
    variants: {
      variant: {
        solid: "",
        outlined:
          "shadow-[0px_0px_0px_1px_rgba(35,34,34,0.08),_0px_1px_1px_0px_rgba(35,34,34,0.05),_0px_2px_4px_0px_rgba(35,34,34,0.08)]",
        plain: "",
      },
      color: {
        accent: "",
        neutral: "",
        red: "",
      },
      size: {
        small: "rounded-md px-2.5 py-1",
        base: "rounded-md px-3 py-1.5",
        large: "text-ng-base rounded-lg px-3.5 py-2",
      },
      disabled: {
        true: "cursor-default opacity-50",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "solid",
        color: "accent",
        className:
          "bg-accent-800 hover:data-[disabled=false]:bg-accent-900 group-data-[state=open]:bg-accent-900 text-white",
      },
      {
        variant: "outlined",
        color: "neutral",
        className:
          "bg-white hover:data-[disabled=false]:bg-neutral-100 group-data-[state=open]:bg-neutral-100",
      },
      {
        variant: "plain",
        color: "neutral",
        className:
          "bg-transparent shadow-none hover:data-[disabled=false]:bg-neutral-100 group-data-[state=open]:bg-neutral-100",
      },
      {
        variant: "solid",
        color: "red",
        className:
          "bg-ng-red-800 hover:data-[disabled=false]:bg-ng-red-900 group-data-[state=open]:bg-ng-red-900 text-white",
      },
    ],
    defaultVariants: {
      variant: DEFAULT_VARIANT,
      color: DEFAULT_COLOR,
      size: DEFAULT_SIZE,
      disabled: DEFAULT_IS_DISABLED,
    },
  },
);

const iconClassNames = cva("size-4 shrink-0", {
  variants: {
    variant: {
      solid: "",
      outlined: "text-neutral-550",
      plain: "text-neutral-550",
    },
    color: {
      accent: "",
      neutral: "",
      red: "",
    },
    size: {
      small: "size-4",
      base: "",
      large: "size-[1.125rem]",
    },
  },
});

const loader = cva("size-5", {
  variants: {
    variant: {
      solid: "",
      outlined: "",
      plain: "",
    },
    color: {
      accent: "",
      neutral: "",
      red: "",
    },
    size: {
      small: "size-4",
      base: "",
      large: "",
    },
  },
  compoundVariants: [
    {
      variant: "solid",
      color: "accent",
      className: "text-accent-300 fill-white",
    },
    {
      variant: "solid",
      color: "red",
      className: "text-ng-red-300 fill-white",
    },
  ],
});

type ButtonVariantProps = VariantProps<typeof button>;

interface NgButtonBaseProps extends ButtonVariantProps {
  startIcon?: React.ReactElement<{ className?: string }>;
  endIcon?: React.ReactElement<{ className?: string }>;
  /**
   * Automatically disables the button when true
   */
  isLoading?: boolean;
  className?: string;
  classNames?: {
    loader?: string;
  };
  children: ReactNode;
  onClick?: (event: React.MouseEvent) => void;
}

/**
 * Polymorphic component type that allows rendering as different HTML elements.
 * Use the `as` prop to specify the element (defaults to "button").
 * Example: <NgButton as="a" href="/path">Link</NgButton>
 */
export type NgButtonProps<C extends ElementType = "button"> =
  NgButtonBaseProps & {
    as?: C;
  } & Omit<ComponentProps<C>, keyof NgButtonBaseProps | "as">;

export const NgButton = <C extends ElementType = "button">({
  as,
  variant = DEFAULT_VARIANT,
  color = DEFAULT_COLOR,
  size = DEFAULT_SIZE,
  startIcon,
  endIcon,
  type = "button",
  disabled = DEFAULT_IS_DISABLED,
  isLoading = false,
  className,
  classNames,
  onClick,
  children,
  ...rest
}: NgButtonProps<C>) => {
  const Component = as || "button";

  const styledStartIcon = startIcon
    ? cloneElement(startIcon, {
        className: cn(
          iconClassNames({ size, variant, color }),
          startIcon.props.className,
        ),
      })
    : null;

  const styledEndIcon = endIcon
    ? cloneElement(endIcon, {
        className: cn(
          iconClassNames({ size, variant, color }),
          endIcon.props.className,
        ),
      })
    : null;

  const showLoading = useDelayedLoading({ isLoading });

  const isDisabled = disabled || showLoading;

  return (
    <Component
      type={Component === "button" ? type : undefined}
      className={cn(
        button({
          variant,
          color,
          size,
          disabled: isDisabled,
          className,
        }),
      )}
      disabled={Component === "button" ? isDisabled : undefined}
      data-disabled={isDisabled}
      onClick={(event: React.MouseEvent) => {
        // Prevents link buttons from navigating when the button is disabled
        // We do this instead of setting pointer-events-none in the variant styles
        // as pointer-events-none breaks tooltips and other interactions with components that wrap the button
        if (isDisabled) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        onClick?.(event);
      }}
      {...rest}
    >
      {styledStartIcon}
      {showLoading ? (
        <Loader
          // Passing null here allows the loader to inherit the text colors from the inlineAction.
          color={null}
          classNames={{
            svg: cn(loader({ className: classNames?.loader })),
          }}
        />
      ) : (
        children
      )}
      {styledEndIcon}
    </Component>
  );
};

NgButton.displayName = "NgButton";
